/**
 * <emojo-readmore> — collapse a long block of merchant prose behind a button.
 *
 * Progressive enhancement, deliberately. The markup ships fully expanded with
 * the button hidden: without JS, and for anything crawling the page, the whole
 * description is present and readable. Collapsing is something this script does
 * to a working page, never something the page depends on.
 *
 * It also refuses to collapse content that barely exceeds the clamp — hiding
 * two lines behind a button costs a reader more than it saves. And once the
 * reader has opened it, it never closes itself: a late-loading image must not
 * swallow the paragraph someone is mid-way through.
 *
 * No dependencies, ~60 lines. The theme carries no JS framework and this is not
 * the thing to introduce one for.
 */
class EmojoReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-readmore-content]');
    this.button = this.querySelector('[data-readmore-toggle]');
    if (!this.content || !this.button) return;

    this.clamp = parseInt(this.dataset.clamp, 10) || 460;
    /* Below this much overflow the button is not worth its own height. */
    this.slack = 140;
    this.opened = false;

    this.style.setProperty('--desc-clamp', this.clamp + 'px');
    this.button.addEventListener('click', () => this.toggle());

    this.measure();

    /* Images and web fonts land after this runs, and both change the height.
       Re-measuring keeps the button honest — but only while still collapsed. */
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => {
        if (!this.opened) this.measure();
      });
      this.observer.observe(this.content);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  measure() {
    /* scrollHeight is the full content height whether or not it is clamped. */
    const full = this.content.scrollHeight;
    if (full <= this.clamp + this.slack) {
      this.removeAttribute('data-collapsed');
      this.button.hidden = true;
      return;
    }
    this.button.hidden = false;
    this.setAttribute('data-collapsed', '');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.textContent = this.dataset.labelMore || 'Read more';
  }

  toggle() {
    const collapsed = this.hasAttribute('data-collapsed');
    if (collapsed) {
      this.opened = true;
      this.removeAttribute('data-collapsed');
      this.button.setAttribute('aria-expanded', 'true');
      this.button.textContent = this.dataset.labelLess || 'Show less';
    } else {
      this.setAttribute('data-collapsed', '');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.textContent = this.dataset.labelMore || 'Read more';
      /* Collapsing pulls the page up under the reader. If the top of the block
         has scrolled off, bring it back rather than dumping them mid-page. */
      if (this.getBoundingClientRect().top < 0) {
        this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }
}

if (!customElements.get('emojo-readmore')) {
  customElements.define('emojo-readmore', EmojoReadMore);
}
